/**
 * Biblioteca de templates de prompt para geração de imagem.
 * Cada função monta um prompt rico (estilo direction + lighting + lens + color grade)
 * a partir de inputs simples. Use direto OU passe por `improveImagePromptAI()` pra
 * deixar ainda mais cinematográfico.
 */

export interface BrandPaletteHint {
  primary?: string;
  secondary?: string;
  accent?: string;
  font?: string;
  tone?: string;
}

/** Cláusulas globais que aumentam qualidade em qualquer prompt. */
const QUALITY_SUFFIX =
  "Editorial photography, magazine-grade composition, perfect lighting, ultra sharp, " +
  "shallow depth of field where appropriate, premium production value, professional color grade, " +
  "no text overlay, no watermark, no generic stock look, no overcrowded composition.";

const ANTI = "Avoid: distorted faces, extra fingers, weird anatomy, generic AI aesthetics, " +
  "cheap cgi feel, low-poly look, lens flare overuse, harsh oversaturation, cartoonish proportions.";

const paletteClause = (b?: BrandPaletteHint) =>
  b
    ? `Brand palette as subtle accents (do NOT paint everything in these): primary ${b.primary ?? "—"}, ` +
      `secondary ${b.secondary ?? "—"}, accent ${b.accent ?? "—"}.`
    : "Neutral cinematic palette.";

/* ====================================================================
   TEMPLATES
   ==================================================================== */

/** Retrato editorial Bauhaus pra avatar de persona. */
export function avatarPersonaPrompt(persona: { name: string; bio: string }, brand?: BrandPaletteHint) {
  return `
EDITORIAL PORTRAIT of ${persona.name}, head and shoulders, centered composition,
shot on Leica Q3 28mm, f/2 shallow depth of field, soft directional sidelight (Rembrandt key),
muted cinematic color grade, monochromatic warm-paper background (#F2EEE6 hint),
subject expression: confident, slightly serious, intelligent.
Reference style: Bauhaus modernism meets Hasselblad portraiture meets New York Times Magazine cover.
Context (subtle hint, do NOT depict literally): ${persona.bio}
${paletteClause(brand)}
Subject must look like a real, dignified individual — not a caricature, not a 3D render.
${QUALITY_SUFFIX}
${ANTI}
`.replace(/\s+/g, " ").trim();
}

/** Garoto/garota propaganda do brand kit. */
export function spokespersonPrompt(opts: {
  clientName: string;
  segment: string;
  persona?: string;
  audience: string;
  brand: BrandPaletteHint;
}) {
  return `
CINEMATIC ENDORSER PORTRAIT for the brand "${opts.clientName}" (${opts.segment}).
Half-body shot, slight 3/4 angle, eyes engaging the lens with quiet authority.
Lighting: large soft sidelight + subtle rim light from behind for separation, mood golden-hour-ish.
Lens: 85mm f/1.4, creamy bokeh, perfect skin texture (not airbrushed), naturalistic.
Wardrobe and styling MUST FIT the audience: ${opts.audience}.
${opts.persona ? `Embodies persona: ${opts.persona}.` : ""}
Background: minimal architectural set, slightly out of focus, hinting at the brand mood.
${paletteClause(opts.brand)} — use palette as wardrobe/prop accents, NOT background flood.
Aesthetic: between Annie Leibovitz, Mario Sorrenti, and a Bottega Veneta campaign.
Subject must look 100% human and real, never stylized illustration.
${QUALITY_SUFFIX}
${ANTI}
`.replace(/\s+/g, " ").trim();
}

/** Logotipo profissional. */
export function logoPrompt(opts: {
  clientName: string;
  segment: string;
  pitch: string;
  brand: BrandPaletteHint;
}) {
  return `
PROFESSIONAL VECTOR LOGO for the brand "${opts.clientName}" (${opts.segment}).
Brand essence: ${opts.pitch}.
Style: minimalist, geometric, vector-clean, single-color or two-color, premium identity.
Format: centered on pure white background, square crop, 1:1 ratio, large negative margin.
Hierarchy: clear symbol/icon + brand name in coherent typography below or beside it.
Primary color: ${opts.brand.primary ?? "#121214"}; accent: ${opts.brand.secondary ?? "—"}.
References: Pentagram, Sagmeister, Vignelli, Karl Gerstner. Swiss / Bauhaus discipline.
NO MOCKUP, no business card display, no t-shirt, no 3d render, no environment.
Just the logo, flat, ready for production use.
NO PHOTOREALISM. Pure graphic design.
${ANTI}
`.replace(/\s+/g, " ").trim();
}

/** Criativo de demanda (post, anúncio, peça gráfica). */
export function creativeAdPrompt(opts: {
  taskBrief: string;
  agentVoice: string;
  format: string;          // "Instagram Reel cover", "Carrossel slide 1", "VSL thumbnail", etc
  aspect: "1:1" | "9:16" | "16:9" | "4:5";
  brand: BrandPaletteHint;
  spokespersonUrl?: string;
  audience?: string;
}) {
  const aspectGuide = {
    "1:1":  "square 1:1 composition, centered subject with breathing room top and bottom",
    "9:16": "vertical 9:16 mobile-first composition, subject upper-third, room for caption at bottom",
    "16:9": "horizontal 16:9 cinematic widescreen, rule-of-thirds composition",
    "4:5":  "vertical 4:5 portrait crop optimized for Instagram feed, subject centered slightly above middle"
  }[opts.aspect];

  return `
EDITORIAL ADVERTISING IMAGE — ${opts.format}.
Brief: ${opts.taskBrief}
Aspect: ${aspectGuide}.
Aesthetic direction: ${opts.agentVoice}. Premium, intentional, never generic.
${opts.audience ? `Target audience cue: ${opts.audience}.` : ""}
${opts.spokespersonUrl ? "Brand spokesperson provided as reference — preserve facial features and styling consistency." : ""}
Lighting: cinema-quality, dramatic but motivated, color-graded for emotional weight.
${paletteClause(opts.brand)}
Composition: leave clear negative space for future copy overlay (do NOT add text to the image).
References: a still from a Netflix premium series + an editorial fashion shoot + a Patagonia campaign.
${QUALITY_SUFFIX}
${ANTI}
`.replace(/\s+/g, " ").trim();
}

/** Ilustração pra slide com direção de arte explícita + logo + spokesperson. */
export function slideIllustrationWithDirection(opts: {
  slideIndex: number;       // 1-based
  slideTitle: string;
  concept: string;
  layout: "cover" | "section" | "bullets" | "image-left" | "image-right" | "metric" | "quote" | "closing";
  brand: BrandPaletteHint;
  art: {
    photoStyle: string;
    paletteIntensity: string;
    aesthetic: string;
    mood: string;
    avoid: string[];
    logoOnThisSlide: boolean;
    logoPlacement: string;
    logoOpacity: number;
    spokespersonOnThisSlide: boolean;
    spokespersonAngle: string;
    spokespersonMood: string;
  };
}) {
  const layoutCue =
    opts.layout === "cover"   ? "powerful establishing hero shot, room for big title overlay on bottom-left"
  : opts.layout === "section" ? "moody atmospheric divider image, almost abstract suggestion"
  : opts.layout === "metric"  ? "clean conceptual visual hinting at scale/transformation"
  : opts.layout === "quote"   ? "evocative human moment, contemplative"
  : opts.layout === "closing" ? "forward-looking horizon imagery, optimistic but earned"
  : "supporting photograph, subject right-of-center, left negative space for text overlay";

  const paletteClause =
    opts.art.paletteIntensity === "saturated"
      ? `Strong brand presence: primary ${opts.brand.primary} + secondary ${opts.brand.secondary} ARE the dominant color story.`
    : opts.art.paletteIntensity === "monochromatic"
      ? `Near-monochromatic palette dominated by ${opts.brand.primary} — minimal hue, maximum contrast.`
    : opts.art.paletteIntensity === "minimal-accent"
      ? `Neutral cinematic palette with only TINY touches of ${opts.brand.secondary} as wardrobe/prop accents.`
    : `Balanced palette: ${opts.brand.primary} as undertone, ${opts.brand.secondary} as accents, ${opts.brand.accent} as breathing space.`;

  const spokesClause = opts.art.spokespersonOnThisSlide
    ? `IMPORTANT: spokesperson photo provided as reference image — PRESERVE FACIAL FEATURES exactly. ` +
      `Angle: ${opts.art.spokespersonAngle}. Mood: ${opts.art.spokespersonMood}. ` +
      `Make them the visual anchor of this slide.`
    : "";

  const logoClause = opts.art.logoOnThisSlide
    ? `IMPORTANT: logo provided as reference — embed it ${opts.art.logoPlacement} at ${Math.round(opts.art.logoOpacity * 100)}% opacity. ` +
      `It should be RECOGNIZABLE but not dominate the composition.`
    : "Do NOT include any logo or brand mark.";

  const avoidClause = opts.art.avoid.length
    ? `STRICTLY AVOID: ${opts.art.avoid.join(", ")}.`
    : "";

  return `
SLIDE ${opts.slideIndex} ILLUSTRATION — "${opts.slideTitle}".

ART DIRECTION (set by senior creative direction):
- Photo style: ${opts.art.photoStyle}
- Aesthetic: ${opts.art.aesthetic}
- Mood: ${opts.art.mood}
- ${paletteClause}

LAYOUT INTENT: ${layoutCue}
CONCEPT TO COMMUNICATE: ${opts.concept}

${spokesClause}

${logoClause}

${avoidClause}

Lighting: cinematic, motivated. Composition: editorial photography, magazine-grade, premium production.
No text overlay (text added later in layout). Leave clear negative space for typography.
Avoid: distorted faces, extra fingers, generic AI aesthetics, cheap cgi feel, cartoonish proportions.
`.replace(/\s+/g, " ").trim();
}

/** Ilustração pra slide de apresentação (deck PPTX). */
export function slideIllustrationPrompt(opts: {
  slideTitle: string;
  concept: string;          // o que o slide quer comunicar
  layout: "cover" | "section" | "bullets" | "image-left" | "image-right" | "metric" | "quote" | "closing";
  brand: BrandPaletteHint;
}) {
  const layoutCue =
    opts.layout === "cover"   ? "powerful establishing shot, wide composition, room for big title overlay"
  : opts.layout === "section" ? "moody atmospheric divider image, almost abstract, suggestion not literal"
  : opts.layout === "metric"  ? "clean conceptual visual that hints at growth/scale, minimal subject"
  : opts.layout === "quote"   ? "evocative human moment that resonates with the quote feeling"
  : opts.layout === "closing" ? "forward-looking horizon imagery, optimistic but earned"
  : "supporting illustrative photograph, subject right-of-center, left negative space for text";

  return `
SLIDE ILLUSTRATION for "${opts.slideTitle}".
Concept to communicate (visually, not literally): ${opts.concept}.
Layout cue: ${layoutCue}.
Style: editorial photography meets boutique design studio reportage.
Lighting: motivated and cinematic — golden, blue hour, or dramatic interior depending on mood.
${paletteClause(opts.brand)}
16:9 widescreen, magazine-grade, premium production.
The slide will overlay text on top — leave clear negative space, do not embed any text in the image itself.
${QUALITY_SUFFIX}
${ANTI}
`.replace(/\s+/g, " ").trim();
}

/** Pôster / peça gráfica de marca. */
export function posterPrompt(opts: {
  concept: string;
  brand: BrandPaletteHint;
}) {
  return `
GRAPHIC POSTER, vertical format (3:4 or A2), brutalist-meets-swiss editorial style.
Concept: ${opts.concept}.
Composition: dominant typographic moment (do NOT generate text, just leave the layout space for it),
strong negative space, geometric block of color, single hero image or graphic shape.
${paletteClause(opts.brand)}
References: Paula Scher Pentagram posters, polish poster school, Werkplaats Typografie, Bureau Borsche.
Texture: subtle paper grain, matte print finish feel.
${QUALITY_SUFFIX}
${ANTI}
`.replace(/\s+/g, " ").trim();
}
